import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Navbar from '../Home/Navbar';
import './AddPost.css';

const API_URL = process.env.REACT_APP_API_URL;


function AddPost() {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    mainContent: "",
    author: "",
    aboutAuthor: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState("");
  const [deleteSearch, setDeleteSearch] = useState("");
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editSearch, setEditSearch] = useState("");
  const [filteredEditPosts, setFilteredEditPosts] = useState([]);
  const [showEditDropdown, setShowEditDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const [isFormValid, setIsFormValid] = useState(false);

  // Load posts (stable callback)
  const loadPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/posts/`);
      setPosts(res.data || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
      alert("❌ Failed to fetch posts.");
    }
  }, []);

  // Fetch posts on component mount
  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Update filtered posts whenever posts or search changes
  useEffect(() => {
    const term = deleteSearch || "";
    if (!term.trim()) {
      setFilteredPosts([]);
      return;
    }
    const filtered = posts.filter(post =>
      post.title.toLowerCase().includes(term.toLowerCase()) ||
      post.category.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredPosts(filtered);
  }, [posts, deleteSearch]);

  // Update filtered edit posts whenever posts or edit search changes
  useEffect(() => {
    const term = editSearch || "";
    if (!term.trim()) {
      setFilteredEditPosts([]);
      return;
    }
    const filtered = posts.filter(post =>
      post.title.toLowerCase().includes(term.toLowerCase()) ||
      post.category.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredEditPosts(filtered);
  }, [posts, editSearch]);

  // Check form validity for mandatory fields
  useEffect(() => {
    const isValid = formData.title.trim() && formData.description.trim() && formData.category && formData.mainContent.trim() && formData.author.trim();
    setIsFormValid(isValid);
  }, [formData]);

  const handleDeleteSearch = (e) => {
    const searchTerm = e.target.value;
    setDeleteSearch(searchTerm);
    setSelectedPostId("");
    setShowDropdown(true);
    // filtering handled by effect watching `posts` and `deleteSearch`
  };

  const handleEditSearch = (e) => {
    const searchTerm = e.target.value;
    setEditSearch(searchTerm);
    setEditingPostId(null);
    setShowEditDropdown(true);
  };

  const handleSelectPost = (postId, postTitle) => {
    setSelectedPostId(postId);
    setDeleteSearch(postTitle);
    setShowDropdown(false);
  };

  const handleSelectEditPost = async (postId, postTitle) => {
    try {
      const res = await axios.get(`${API_URL}/posts/${postId}`);
      const post = res.data;
      setFormData({
        title: post.title,
        description: post.description,
        category: post.category,
        mainContent: post.mainContent,
        author: post.author,
        aboutAuthor: post.aboutAuthor,
      });
      setImagePreview(post.image ? `${API_URL}/images/${post.image}` : null);
      setImageFile(null);
      setIsEditing(true);
      setEditingPostId(postId);
      setEditSearch(postTitle);
      setShowEditDropdown(false);
    } catch (error) {
      console.error("Error fetching post for edit:", error);
      alert("❌ Failed to load post for editing.");
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPostId) {
      alert("⚠️ Please select a post to delete.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this post?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/posts/${selectedPostId}`);
      alert("✅ Post deleted successfully!");
      setSelectedPostId("");
      setDeleteSearch("");
      // Refresh the list and force empty search so filteredPosts updates immediately
      await loadPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      alert("❌ Failed to delete post. Check console for details.");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg'];
      if (!validTypes.includes(file.type)) {
        alert("❌ Please upload only PNG or JPG files.");
        return;
      }
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReset = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      mainContent: "",
      author: "",
      aboutAuthor: "",
    });
    removeImage();
    setIsEditing(false);
    setEditingPostId(null);
    setEditSearch("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

  // Validate required fields
  if (!formData.title || !formData.description || !formData.category || !formData.mainContent || !formData.author) {
    alert("⚠️ Please fill in all required fields.");
    return;
  }

  // Create FormData to send both text and file (if image selected)
  const submitData = new FormData();
  submitData.append('title', formData.title);
  submitData.append('description', formData.description);
  submitData.append('category', formData.category);
  submitData.append('mainContent', formData.mainContent);
  submitData.append('author', formData.author);
  submitData.append('aboutAuthor', formData.aboutAuthor);
  if (imageFile) {
    submitData.append('image', imageFile);
  }

  // DEBUG: log FormData entries so we can see what is actually being sent
  try {
    if (imageFile) {
      console.group('FormData contents (multipart)');
      for (const pair of submitData.entries()) {
        // For files, log name and file info
        if (pair[1] instanceof File) {
          console.log(pair[0], { name: pair[1].name, type: pair[1].type, size: pair[1].size });
        } else {
          console.log(pair[0], pair[1]);
        }
      }
      console.groupEnd();
    } else {
      // If no image, log the JSON object we'll send
      const jsonPayload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        mainContent: formData.mainContent,
        author: formData.author,
        aboutAuthor: formData.aboutAuthor,
      };
      console.log('JSON payload', jsonPayload);
    }

    let res;
    if (isEditing) {
      res = await axios.put(`${API_URL}/posts/${editingPostId}`, imageFile ? submitData : {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        mainContent: formData.mainContent,
        author: formData.author,
        aboutAuthor: formData.aboutAuthor,
      });
      alert("✅ Post updated successfully!");
    } else {
      res = await axios.post(`${API_URL}/posts/`, imageFile ? submitData : {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        mainContent: formData.mainContent,
        author: formData.author,
        aboutAuthor: formData.aboutAuthor,
      });
      alert("✅ Post created successfully!");
    }

    console.log(res.data);
    setFormData({
      title: "",
      description: "",
      category: "",
      mainContent: "",
      author: "",
      aboutAuthor: "",
    });
    removeImage();
    setIsEditing(false);
    setEditingPostId(null);
    setEditSearch("");
    await loadPosts(); // Refresh the list
  } catch (error) {
    // Improved error logging
    console.error('Submit error:', error);
    if (error.response) {
      console.error('Server response:', error.response.data);
      alert(`❌ Failed to ${isEditing ? 'update' : 'create'} post: ${JSON.stringify(error.response.data)}`);
    } else {
      alert(`❌ Failed to ${isEditing ? 'update' : 'create'} post. Check console for details.`);
    }
  }
};
  return (
    <>
      <Navbar />
      <div className="add-post-container">
        <h2>{isEditing ? "Edit Blog Post" : "Create a New Blog Post"}</h2>
        <form className="add-post-form" onSubmit={handleSubmit}>
          <label>Title</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} required />

          <label>Description</label>
          <input type="text" name="description" value={formData.description} onChange={handleChange} required />

          <label>Category</label>
          <select name="category" value={formData.category} onChange={handleChange} required>
            <option value="">Select Category</option>
            <option value="Caste">Caste</option>
            <option value="Gender">Gender</option>
            <option value="God and Religion">God</option>
            <option value="Culture">Culture</option>
            <option value="Reservation">Reservation</option>
            <option value="Fact Check">Fact Check</option>
            <option value="Discussion">Discussion</option>
            <option value="Current Affairs">Current Affairs</option>
          </select>

          <label>Main Content</label>
          <textarea
            name="mainContent"
            value={formData.mainContent}
            onChange={handleChange}
            rows="5"
            required
          ></textarea>

          <label>Author</label>
          <input type="text" name="author" value={formData.author} onChange={handleChange} required />

          <label>About Author</label>
          <input type="text" name="aboutAuthor" value={formData.aboutAuthor} onChange={handleChange} />

          <label>Upload Image (PNG or JPG)</label>
          <input 
            ref={fileInputRef}
            type="file" 
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            onChange={handleImageChange}
            className="image-upload-input"
          />

          {imagePreview && (
            <div className="image-preview-container">
              <img src={imagePreview} alt="Preview" className="image-preview" />
              <button
                type="button"
                onClick={removeImage}
                className="remove-image-btn"
              >
                Remove Image
              </button>
            </div>
          )}

          <div className="button-container">
            <button type="submit" disabled={!isFormValid}>{isEditing ? "UPDATE" : "POST"}</button>
            <button type="button" onClick={handleReset} className="reset-button">RESET</button>
          </div>
        </form>
      </div>

      <div className="delete-post-container">
        <h2>Delete a Post</h2>
        <div className="delete-form">
          <label htmlFor="delete-search">Search and Select Post</label>
          <div className="delete-search-wrapper">
            <input
              id="delete-search"
              type="text"
              placeholder="Search by title or category..."
              value={deleteSearch}
              onChange={handleDeleteSearch}
              onFocus={() => setShowDropdown(true)}
              className="delete-search-input"
            />
            {showDropdown && filteredPosts.length > 0 && (
              <div className="delete-dropdown-list">
                {filteredPosts.map(post => (
                  <div
                    key={post._id}
                    className="dropdown-item"
                    onClick={() => handleSelectPost(post._id, post.title)}
                  >
                    <div className="dropdown-item-title">{post.title}</div>
                    <div className="dropdown-item-category">{post.category}</div>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && deleteSearch && filteredPosts.length === 0 && (
              <div className="delete-dropdown-list">
                <div className="dropdown-item-empty">No posts found</div>
              </div>
            )}
          </div>

          {selectedPostId && (
            <p className="selected-post-info">✓ Selected: {deleteSearch}</p>
          )}

          <button
            type="button"
            onClick={handleDeletePost}
            className="delete-button"
            disabled={!selectedPostId}
          >
            DELETE POST
          </button>
        </div>
      </div>

      <div className="edit-post-container">
        <h2>Edit a Post</h2>
        <div className="edit-form">
          <label htmlFor="edit-search">Search and Select Post to Edit</label>
          <div className="edit-search-wrapper">
            <input
              id="edit-search"
              type="text"
              placeholder="Search by title or category..."
              value={editSearch}
              onChange={handleEditSearch}
              onFocus={() => setShowEditDropdown(true)}
              className="edit-search-input"
            />
            {showEditDropdown && filteredEditPosts.length > 0 && (
              <div className="edit-dropdown-list">
                {filteredEditPosts.map(post => (
                  <div
                    key={post._id}
                    className="dropdown-item"
                    onClick={() => handleSelectEditPost(post._id, post.title)}
                  >
                    <div className="dropdown-item-title">{post.title}</div>
                    <div className="dropdown-item-category">{post.category}</div>
                  </div>
                ))}
              </div>
            )}
            {showEditDropdown && editSearch && filteredEditPosts.length === 0 && (
              <div className="edit-dropdown-list">
                <div className="dropdown-item-empty">No posts found</div>
              </div>
            )}
          </div>

          {editingPostId && (
            <p className="selected-post-info">✓ Selected for edit: {editSearch}</p>
          )}
        </div>
      </div>
    </>
  );
}

export default AddPost;
